import {useEffect, useRef, useState} from "react";
import {Box, Grid, IconButton, Snackbar, Typography} from "@mui/material";
import {useParams} from 'react-router';
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import DownloadIcon from "@mui/icons-material/Download";
import DeleteIcon from "@mui/icons-material/Delete";
import {createJobDocument, deleteJobDocument, downloadJobDocument, fetchJobDocuments} from "../../../../../utils/apiCalls";
import {ConfirmDialog, StyledButtonContained, useStyles} from "../../../../../utils/uiUtils";
import DataTable from "../../../../../component/DataTable/DataTable";
import {ORDER_DOCUMENTS_TABLE_COLUMNS} from "./jobDocumentsTableConfig";

const itemSx = {display: 'flex', flexDirection: 'column', alignItems: 'center'};

export const OrderDocuments = ({orderDetail, canDelete = false, orderId: orderIdProp, showHeading = false, enableUpload = false}) => {
    const params = useParams();
    const orderId = orderIdProp || params.orderId;
    const classes = useStyles();

    const [documents, setDocuments] = useState([]);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
    const [uploading, setUploading] = useState(false);
    const fileInputRef = useRef(null);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarSeverity, setSnackbarSeverity] = useState('success');

    const openSnackbar = (msg, severity = 'success') => {
        setSnackbarMessage(msg);
        setSnackbarSeverity(severity);
        setSnackbarOpen(true);
    };
    const closeSnackbar = () => setSnackbarOpen(false);

    useEffect(() => {
        loadDocuments();
    }, []);

    const loadDocuments = () => {
        fetchJobDocuments(orderId)
            .then(data => {
                if (!data) { openSnackbar('Failed to load documents.', 'error'); return; }
                const results = (data?.results || []).map(doc => ({...doc, item_id: doc.uuid}));
                setDocuments(results);
            })
            .catch(() => openSnackbar('Failed to load documents.', 'error'));
    };

    const handleView = (row) => {
        downloadJobDocument(orderId, row.uuid)
            .then(blob => {
                const blobUrl = URL.createObjectURL(blob);
                const newTab = window.open('', '_blank');
                if (!newTab) { openSnackbar('Pop-up blocked. Please allow pop-ups for this site.', 'error'); return; }
                newTab.location.href = blobUrl;
            })
            .catch(err => openSnackbar(`Failed to open file: ${err.message}`, 'error'));
    };

    const handleDownload = (row) => {
        downloadJobDocument(orderId, row.uuid)
            .then(blob => {
                const blobUrl = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = blobUrl;
                a.download = row.name || 'document';
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                URL.revokeObjectURL(blobUrl);
            })
            .catch(err => openSnackbar(`Failed to download file: ${err.message}`, 'error'));
    };

    const handleDeleteClick = (row) => {
        setDeleteTarget(row);
        setConfirmDeleteOpen(true);
    };

    const handleConfirmDelete = () => {
        deleteJobDocument(orderId, deleteTarget.uuid)
            .then(() => {
                setConfirmDeleteOpen(false);
                setDeleteTarget(null);
                loadDocuments();
                openSnackbar('Document deleted successfully.');
            })
            .catch(() => {
                setConfirmDeleteOpen(false);
                openSnackbar('Failed to delete document.', 'error');
            });
    };

    const handleUpload = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        setUploading(true);
        const reader = new FileReader();
        reader.onload = (event) => {
            const base64 = event.target.result.split(',')[1];
            createJobDocument(orderId, {
                name: file.name,
                file: base64,
                file_type: 'user_tape_list',
                order: orderId,
            })
                .then(() => {
                    loadDocuments();
                    fileInputRef.current.value = '';
                    openSnackbar('Document uploaded successfully.');
                })
                .catch(() => openSnackbar('Failed to upload document.', 'error'))
                .finally(() => setUploading(false));
        };
        reader.readAsDataURL(file);
    };

    const columnsConfig = ORDER_DOCUMENTS_TABLE_COLUMNS.map(col => {
        if (col.name !== "Actions") return col;
        return {
            ...col,
            custom_render: (row) => (
                <>
                    <Box sx={itemSx}>
                        <IconButton onClick={() => handleView(row)} size="small">
                            <VisibilityOutlinedIcon sx={{color: '#FFFFFF', '&:hover': {color: '#FFC540'}}}/>
                        </IconButton>
                        <Typography sx={{color: '#FFFFFF', fontSize: 12}}>View</Typography>
                    </Box>
                    <Box sx={itemSx}>
                        <IconButton onClick={() => handleDownload(row)} size="small">
                            <DownloadIcon sx={{color: '#FFFFFF', '&:hover': {color: '#FFC540'}}}/>
                        </IconButton>
                        <Typography sx={{color: '#FFFFFF', fontSize: 12}}>Download</Typography>
                    </Box>
                    {canDelete && (
                        <Box sx={itemSx}>
                            <IconButton onClick={() => handleDeleteClick(row)} size="small">
                                <DeleteIcon sx={{color: '#FFFFFF', '&:hover': {color: '#FFC540'}}}/>
                            </IconButton>
                            <Typography sx={{color: '#FFFFFF', fontSize: 12}}>Delete</Typography>
                        </Box>
                    )}
                </>
            )
        };
    });

    return (
        <Box p={2}>
            <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2}}>
                {showHeading ? <Typography className={classes.headingLarge}>Documents</Typography> : <Box/>}
                {enableUpload && (
                    <Box>
                        <input
                            ref={fileInputRef}
                            type="file"
                            style={{display: 'none'}}
                            onChange={handleUpload}
                        />
                        <StyledButtonContained
                            variant="contained"
                            disabled={uploading}
                            onClick={() => fileInputRef.current.click()}
                        >
                            {uploading ? 'Uploading...' : 'Upload Document'}
                        </StyledButtonContained>
                    </Box>
                )}
            </Box>
            <DataTable
                data={documents}
                columnsConfig={columnsConfig}
                dataContainsFormatting={false}
                showPagination={false}
            />

            <Snackbar
                anchorOrigin={{vertical: 'top', horizontal: 'right'}}
                open={snackbarOpen}
                onClose={closeSnackbar}
                message={snackbarMessage}
                autoHideDuration={5000}
                ContentProps={{
                    sx: snackbarSeverity === 'error'
                        ? {backgroundColor: '#FF6C65', color: '#FFFFFF'}
                        : {}
                }}
            />

            {canDelete && (
                <ConfirmDialog
                    open={confirmDeleteOpen}
                    onClose={() => setConfirmDeleteOpen(false)}
                    onConfirm={handleConfirmDelete}
                    title="Delete Document"
                    message={`Are you sure you want to delete "${deleteTarget?.name}"? This cannot be undone.`}
                />
            )}
        </Box>
    );
};
